// Скоринг совместимости — 05_compatibility.md ("Астрологическая основа", "Правила и защиты"),
// 09_tech.md §3. seed = hash(dateA, dateB) даёт детерминизм: одна и та же пара дат всегда даёт
// один и тот же результат, повторная проверка не пересчитывает.
import { ZODIAC_SIGNS } from '../enums.js'
import type { CompatType, ZodiacSign } from '../enums.js'
import {
  ASPECT_SCORE_BOUNDS,
  aspectPairScore,
  FIRST_IMPRESSION_SPHERE,
  averagePairScores,
  calcAspectComponent,
  findAspect,
  getSpherePairs,
} from './compatAspects.js'
import type {
  AspectKind,
  CompatPoint,
  CompatPointPositions,
  CompatPointWeights,
} from './compatAspects.js'

// Синастрия считается по пяти "личным" планетам — Юпитер/Сатурн сюда не входят
// (см. 05_compatibility.md "Астрологическая основа").
export type CompatPlanet = 'sun' | 'moon' | 'venus' | 'mars' | 'mercury'

export type CompatSigns = Record<CompatPlanet, ZodiacSign>

/**
 * Точные эклиптические долготы тех же пяти планет, 0..360. Есть только когда известна полная
 * дата рождения второго: в режиме «только знак» градусов взять неоткуда.
 */
export type CompatPositions = Record<CompatPlanet, number>

/**
 * Края шкалы процента.
 *
 * Раньше тут стояли 35 и 98, и это было неправдой: до концов шкалы расчёт не доходил никогда.
 * Обе составляющие имели собственные полы — худшая пара стихий давала не 0, а 0.35, худшая
 * модальность 0.55, — поэтому даже нарочно собранная худшая пара (все пять планет одного
 * в Овне, все пять другого в Раке) выдавала 61, а замер по выборке ложился в 62…86.
 * Две строки-приговора из четырёх человек не мог увидеть в принципе.
 *
 * Теперь обе составляющие нормируются в 0..1 по своим настоящим границам (`SIGN_BOUNDS`,
 * `ASPECT_SCORE_BOUNDS`), а края назначены так, чтобы им было куда упереться: 30 — «сошлись
 * плохо», 100 — «сошлось всё». Астрология от этого не изменилась ни на градус: порядок пар
 * между собой прежний, поменялся только масштаб, в котором он показывается.
 */
const MIN_SCORE = 30
const MAX_SCORE = 100
const SCORE_SPAN = MAX_SCORE - MIN_SCORE

/**
 * Простой строковый хеш (вариант djb2 xor) без зависимостей. Порядок дат важен для конкретного
 * числа, но при одинаковом порядке одна и та же пара дат всегда даёт одно и то же значение —
 * это всё, что требуется от seed.
 */
export const compatSeed = (dateA: string, dateB: string): number => {
  const input = `${dateA}|${dateB}`
  let hash = 5381
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i)
  }
  return hash >>> 0 // приводим к беззнаковому 32-битному числу
}

export type Element = 'fire' | 'earth' | 'air' | 'water'
type Modality = 'cardinal' | 'fixed' | 'mutable'

// Стихии и модальности знаков — классическое деление зодиакального круга.
const ELEMENT_BY_SIGN: Record<ZodiacSign, Element> = {
  aries: 'fire',
  leo: 'fire',
  sagittarius: 'fire',
  taurus: 'earth',
  virgo: 'earth',
  capricorn: 'earth',
  gemini: 'air',
  libra: 'air',
  aquarius: 'air',
  cancer: 'water',
  scorpio: 'water',
  pisces: 'water',
}

const MODALITY_BY_SIGN: Record<ZodiacSign, Modality> = {
  aries: 'cardinal',
  cancer: 'cardinal',
  libra: 'cardinal',
  capricorn: 'cardinal',
  taurus: 'fixed',
  leo: 'fixed',
  scorpio: 'fixed',
  aquarius: 'fixed',
  gemini: 'mutable',
  virgo: 'mutable',
  sagittarius: 'mutable',
  pisces: 'mutable',
}

// Совместимость стихий 0..1: огонь и воздух питают друг друга, земля и вода — тоже;
// огонь/земля и земля/воздух — трение (не дополняют); огонь/вода — сильное, но неровное
// сочетание (пар и конфликт), поэтому ниже, чем "дружественные" пары.
const ELEMENT_COMPAT: Record<Element, Record<Element, number>> = {
  fire: { fire: 1, earth: 0.35, air: 0.9, water: 0.1 },
  earth: { fire: 0.35, earth: 1, air: 0.1, water: 0.85 },
  air: { fire: 0.9, earth: 0.1, air: 1, water: 0.35 },
  water: { fire: 0.1, earth: 0.85, air: 0.35, water: 1 },
}

// Совместимость модальностей 0..1: кардинальный + изменчивый — лидер и тот, кто подстраивается
// (легко); одинаковая модальность — взаимопонимание, но и конкуренция за один и тот же способ
// действовать; фиксированный + любая — упорство, требует больше усилий.
const MODALITY_COMPAT: Record<Modality, Record<Modality, number>> = {
  cardinal: { cardinal: 0.25, fixed: 0.6, mutable: 0.95 },
  fixed: { cardinal: 0.6, fixed: 0.25, mutable: 0.7 },
  mutable: { cardinal: 0.95, fixed: 0.7, mutable: 0.25 },
}

const rawSignPairScore = (a: ZodiacSign, b: ZodiacSign): number => {
  const elementScore = ELEMENT_COMPAT[ELEMENT_BY_SIGN[a]][ELEMENT_BY_SIGN[b]]
  const modalityScore = MODALITY_COMPAT[MODALITY_BY_SIGN[a]][MODALITY_BY_SIGN[b]]
  return elementScore * 0.7 + modalityScore * 0.3
}

/**
 * Настоящие границы оценки пары знаков — перебором всех 144 сочетаний, а не числами руками.
 *
 * Стихия и модальность взвешены 70 на 30, и края их таблиц не совпадают, поэтому вычислить
 * минимум и максимум в уме нельзя, а вписать их константами значит завести ещё одно место,
 * которое молча разъедется с таблицами при первой же правке коэффициентов.
 */
const SIGN_BOUNDS = ((): { min: number; max: number } => {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const a of ZODIAC_SIGNS) {
    for (const b of ZODIAC_SIGNS) {
      const score = rawSignPairScore(a, b)
      if (score < min) min = score
      if (score > max) max = score
    }
  }

  return { min, max }
})()

/** Оценка пары знаков, растянутая на 0..1: 0 — худшее из возможных, 1 — лучшее. */
const signPairScore = (a: ZodiacSign, b: ZodiacSign): number =>
  (rawSignPairScore(a, b) - SIGN_BOUNDS.min) / (SIGN_BOUNDS.max - SIGN_BOUNDS.min)

// Вес планеты в скоринге зависит от линзы: партнёру важнее Венера/Марс, дружбе и
// рабочим линзам — Меркурий (стиль общения), крашу — влечение (Венера/Марс), боссу —
// Солнце и Меркурий (как читает и как разговаривает). Веса каждой линзы суммируются в 1.
const COMPAT_WEIGHTS: Record<CompatType, Record<CompatPlanet, number>> = {
  partner: { sun: 0.15, moon: 0.2, venus: 0.3, mars: 0.25, mercury: 0.1 },
  friend: { sun: 0.25, moon: 0.15, venus: 0.1, mars: 0.1, mercury: 0.4 },
  crush: { sun: 0.15, moon: 0.1, venus: 0.3, mars: 0.35, mercury: 0.1 },
  ex: { sun: 0.2, moon: 0.2, venus: 0.2, mars: 0.2, mercury: 0.2 },
  colleague: { sun: 0.2, moon: 0.1, venus: 0.05, mars: 0.15, mercury: 0.5 },
  boss: { sun: 0.3, moon: 0.1, venus: 0.05, mars: 0.25, mercury: 0.3 },
  // Семейные линзы: главное — Луна (эмоции и дом) и Меркурий (как разговариваете).
  // Венера и Марс почти не участвуют: это не про влечение и не про соперничество за внимание.
  mother: { sun: 0.2, moon: 0.4, venus: 0.1, mars: 0.05, mercury: 0.25 },
  father: { sun: 0.3, moon: 0.25, venus: 0.05, mars: 0.15, mercury: 0.25 },
  sibling: { sun: 0.25, moon: 0.25, venus: 0.05, mars: 0.2, mercury: 0.25 },
  relative: { sun: 0.25, moon: 0.25, venus: 0.1, mars: 0.1, mercury: 0.3 },
}

/**
 * Вес асцендента в углах между картами.
 *
 * Асцендент — не шестая планета, а точка восходящего горизонта: в классической синастрии по нему
 * читают первое впечатление и то, узнаёт ли человек себя рядом со вторым. Отсюда и разброс весов:
 * у половинки и краша ось асцендента — один из главных индикаторов притяжения (0.20, между Луной
 * 0.20 и Марсом 0.25 у половинки); у дружбы и родни она весит заметно меньше характера и манеры
 * говорить (0.12); у коллеги и начальника почти не весит (0.08) — рабочие отношения читаются
 * Меркурием, Солнцем и Марсом, а не тем, как человек выглядит при первой встрече.
 *
 * Веса планет при этом НЕ меняются: сумма пяти планет по-прежнему 1, а асцендент добавляется
 * шестым голосом сверху. Аспектная составляющая — взвешенное среднее по парам, поэтому лишний
 * голос не выбивает её из 0..1 и не сдвигает шкалу; он только уточняет число.
 */
const COMPAT_ASC_WEIGHT: Record<CompatType, number> = {
  partner: 0.2,
  crush: 0.2,
  ex: 0.15,
  friend: 0.12,
  sibling: 0.12,
  relative: 0.12,
  mother: 0.1,
  father: 0.1,
  colleague: 0.08,
  boss: 0.08,
}

export const getCompatAscWeight = (type: CompatType): number => COMPAT_ASC_WEIGHT[type]

export interface CalcCompatScoreInput {
  signsA: CompatSigns
  signsB: CompatSigns
  type: CompatType
  /** Точные долготы обеих карт. Без них считаем только по знакам — режим «только знак». */
  positionsA?: CompatPositions | null
  positionsB?: CompatPositions | null
  /**
   * Долготы асцендентов. Считаются только когда у ОБОИХ известны дата, время и город
   * (`canUseAscendant` в `compatPrecision.ts`); одностороннего асцендента не бывает.
   */
  ascA?: number | null
  ascB?: number | null
}

/**
 * Набор точек одной карты для поиска углов: пять планет плюс асцендент, если он есть у обоих.
 * Именно здесь односторонний асцендент отбрасывается — дальше по коду его уже не существует.
 */
const toPoints = (
  positions: CompatPositions,
  asc: number | null | undefined,
  isAscUsable: boolean,
): CompatPointPositions =>
  isAscUsable && asc !== null && asc !== undefined ? { ...positions, asc } : { ...positions }

const toPointWeights = (type: CompatType, isAscUsable: boolean): CompatPointWeights =>
  isAscUsable
    ? { ...COMPAT_WEIGHTS[type], asc: COMPAT_ASC_WEIGHT[type] }
    : { ...COMPAT_WEIGHTS[type] }

/**
 * Доля знаков и аспектов в итоговом проценте.
 *
 * Знаки — устойчивая опора: они меняются раз в несколько дней и дают паре её «характер».
 * Аспекты — точная часть: они меняются в течение суток и объясняют, почему две пары с
 * одинаковыми знаками живут по-разному. Раньше эту разницу изображал случайный джиттер от
 * хеша дат — теперь её создаёт настоящая геометрия.
 */
const SIGN_WEIGHT = 0.55
const ASPECT_WEIGHT = 0.45

/** Совместимость знаков пяти планет с весами линзы, 0..1. */
const signComponent = (signsA: CompatSigns, signsB: CompatSigns, type: CompatType): number => {
  const weights = COMPAT_WEIGHTS[type]
  const planets = Object.keys(weights) as CompatPlanet[]

  return planets.reduce(
    (sum, planet) => sum + weights[planet] * signPairScore(signsA[planet], signsB[planet]),
    0,
  )
}

/**
 * Компонента углов, растянутая на 0..1 по границам гармоний.
 *
 * Сама она живёт между гармонией самого трудного угла и гармонией самого лёгкого (0.25 и 1),
 * то есть в 0..1 не попадает никогда. Без этого перевода низ шкалы недостижим даже у пары,
 * у которой все связи — точные квадраты.
 */
const normalizeAspects = (value: number): number =>
  (value - ASPECT_SCORE_BOUNDS.min) / (ASPECT_SCORE_BOUNDS.max - ASPECT_SCORE_BOUNDS.min)

/**
 * Растяжка вокруг середины перед переводом в проценты.
 *
 * Нужна, потому что итог стал средневзвешенным по шкалам, а шкала сама по себе — среднее
 * по трём парам точек. Двойное усреднение прижимает всё к середине: без растяжки девять
 * проверок из десяти у половинки давали число между пятьюдесятью и семьюдесятью, минимум
 * на выборке был 47, и «плохих» результатов человек не увидел бы почти никогда.
 *
 * Преобразование линейное и применяется В ОДНОМ месте — `toPercent`, — через которое проходят
 * и шкалы, и итог. Поэтому равенство «целое равно средневзвешенному частей» сохраняется:
 * растянуть среднее и усреднить растянутые — одно и то же, пока преобразование линейно.
 * Значение подобрано по замеру: при 1.5 разброс возвращается к 30..100, ниже пятидесяти
 * попадает каждая десятая пара, выше семидесяти — почти треть (решение основателя).
 */
const SPREAD = 1.5

const stretch = (base: number): number => Math.min(1, Math.max(0, 0.5 + (base - 0.5) * SPREAD))

const toPercent = (base: number): number =>
  Math.min(MAX_SCORE, Math.max(MIN_SCORE, Math.round(MIN_SCORE + stretch(base) * SCORE_SPAN)))

/** Знак точки: у планет он готовый, у асцендента считается из долготы. */
const signAt = (
  point: CompatPoint,
  signs: CompatSigns,
  asc: number | null | undefined,
): ZodiacSign | null => {
  if (point !== 'asc') return signs[point as CompatPlanet]
  if (asc === null || asc === undefined) return null
  const degrees = ((asc % 360) + 360) % 360
  return ZODIAC_SIGNS[Math.floor(degrees / 30)] as ZodiacSign
}

/** Долгота точки: у планет готовая, у асцендента — отдельным аргументом. */
const positionAt = (
  point: CompatPoint,
  positions: CompatPositions,
  asc: number | null | undefined,
): number | null => (point === 'asc' ? (asc ?? null) : positions[point as CompatPlanet])

export interface SphereBase {
  key: string
  /** 0..1 — та же величина, из которой считается процент. */
  base: number
  /** Вес сферы в общем проценте: сумма важностей её пар в этой линзе. */
  weight: number
  aspect: SphereAspect | null
}

/**
 * Значение одной сферы и её вес — общий кирпич для шкал и для итогового процента.
 *
 * ГЛАВНОЕ ПРО УСТРОЙСТВО. Раньше общий процент и шкалы считались по РАЗНЫМ парам точек:
 * итог сравнивал только одноимённые планеты (её Солнце с его Солнцем), а шкалы —
 * перекрёстные (её Венеру с его Марсом). Из двенадцати сравнений в шкалах половинки итог
 * видел четыре, поэтому части спокойно оказывались выше целого, и объяснить это на экране
 * было нечем. Теперь целое собирается ровно из тех частей, которые человек видит
 * (решение основателя).
 *
 * Вес сферы — сумма важностей её пар в линзе, та же арифметика, что и внутри углов. Замысел
 * линзы от этого сохраняется: у половинки «Страсть» на Венере с Марсом весит больше, чем
 * «Разговоры» на Меркурии, — не потому что так решили отдельно, а потому что так заданы веса.
 */
const calcSphereBase = (
  key: string,
  type: CompatType,
  { signsA, signsB, positionsA, positionsB, ascA, ascB }: CalcCompatScoreInput,
): SphereBase | null => {
  const pairs = getSpherePairs(type, key)
  if (pairs.length === 0) return null

  // Первое впечатление считается ТОЛЬКО когда асцендент есть у обоих и обе карты построены
  // на точный момент. Односторонний асцендент поставил бы процент в зависимость от того,
  // чьи данные полнее, а не от самой пары, — и часть связок («мой асцендент к его Солнцу»)
  // просачивалась бы в счёт даже без второго асцендента.
  if (key === FIRST_IMPRESSION_SPHERE) {
    const hasBoth = ascA !== null && ascA !== undefined && ascB !== null && ascB !== undefined
    if (!hasBoth || !positionsA || !positionsB) return null
  }

  const weights = COMPAT_WEIGHTS[type]
  const ascWeight = COMPAT_ASC_WEIGHT[type]
  const weightOf = (point: CompatPoint) =>
    point === 'asc' ? ascWeight : weights[point as CompatPlanet]

  const signScores: number[] = []
  const aspectScores: number[] = []
  let strongest: SphereAspect | null = null
  let weight = 0

  for (const [own, target] of pairs) {
    const ownSign = signAt(own, signsA, ascA)
    const targetSign = signAt(target, signsB, ascB)
    if (!ownSign || !targetSign) continue

    signScores.push(signPairScore(ownSign, targetSign))
    weight += weightOf(own) * weightOf(target)

    if (!positionsA || !positionsB) continue
    const ownLon = positionAt(own, positionsA, ascA)
    const targetLon = positionAt(target, positionsB, ascB)
    if (ownLon === null || targetLon === null) continue

    const hit = findAspect(ownLon, targetLon)
    if (!hit) continue

    aspectScores.push(aspectPairScore(hit))
    if (!strongest || hit.exactness > strongest.exactness) {
      strongest = { own, target, kind: hit.kind, exactness: hit.exactness }
    }
  }

  if (signScores.length === 0) return null

  // Знаки — ПРОСТОЕ среднее, без сглаживания. Сглаживание существует ради углов: их у пары
  // может не быть вовсе, и одна точная связь не должна решать всё. У знаков связь есть всегда
  // и у каждой пары, поэтому подмешивать к ним виртуальную нейтральную пару не за что — это
  // только мешает шкале дойти до краёв. Проверено тестом на крайние точки: со сглаживанием
  // худшая возможная пара переставала давать нижний край шкалы.
  const signBase = signScores.reduce((sum, score) => sum + score, 0) / signScores.length

  // Без градусов углов не существует, и сфера держится на одних знаках — как и общий процент
  // в режиме «только знак». Приписывать сюда нейтральную составляющую нельзя: это добавило бы
  // к числу половину, которой в расчёте нет.
  const base =
    !positionsA || !positionsB
      ? signBase
      : signBase * SIGN_WEIGHT + normalizeAspects(averagePairScores(aspectScores)) * ASPECT_WEIGHT

  return { key, base, weight, aspect: strongest }
}

/** Все сферы линзы, у которых нашлось что считать. */
const calcSphereBases = (type: CompatType, input: CalcCompatScoreInput): SphereBase[] =>
  getSpheresByCompatType(type)
    .map((key) => calcSphereBase(key, type, input))
    .filter((sphere): sphere is SphereBase => sphere !== null)

/**
 * Итоговый процент — средневзвешенное по сферам линзы, растянутое на 30..100.
 *
 * Детерминизм сохраняется без всякого seed: одни и те же две даты дают одни и те же долготы,
 * а значит и один и тот же процент.
 */
export const calcCompatScore = (input: CalcCompatScoreInput): number => {
  const spheres = calcSphereBases(input.type, input)
  const total = spheres.reduce((sum, sphere) => sum + sphere.weight, 0)

  // Вырожденный случай: у линзы не осталось ни одной считаемой сферы. Тогда берём прежнюю
  // опору — совпадение одноимённых точек, — иначе процента не будет вовсе.
  if (spheres.length === 0 || total === 0) {
    return toPercent(signComponent(input.signsA, input.signsB, input.type))
  }

  return toPercent(spheres.reduce((sum, sphere) => sum + sphere.base * sphere.weight, 0) / total)
}

export interface FindAscAspectInput {
  type: CompatType
  positionsA: CompatPositions
  positionsB: CompatPositions
  ascA: number
  ascB: number
}

/**
 * Самый весомый угол, в котором участвует асцендент, — его разбор называет отдельной фразой.
 *
 * В шкалы сфер асцендент не входит: сферы описаны парами планет (`SPHERE_PAIRS`), и вписывать
 * туда шестую точку значило бы переписать конфигурацию десяти линз ради величины, которая есть
 * у меньшинства пар. В общий процент он входит, и человек имеет право увидеть, чем именно
 * обернулось введённое время, — поэтому одна названная связка, а не молчание.
 */
export const findAscAspect = ({
  type,
  positionsA,
  positionsB,
  ascA,
  ascB,
}: FindAscAspectInput): SphereAspect | null => {
  const weights = toPointWeights(type, true)
  const pointsA: CompatPointPositions = { ...positionsA, asc: ascA }
  const pointsB: CompatPointPositions = { ...positionsB, asc: ascB }

  let best: SphereAspect | null = null
  let bestRank = 0

  const consider = (own: CompatPoint, target: CompatPoint) => {
    const hit = findAspect(pointsA[own] as number, pointsB[target] as number)
    if (!hit) return
    // Ранг — вес пары на точность угла: широкий угол между второстепенными точками не должен
    // побеждать точный угол между важными.
    const rank = (weights[own] ?? 0) * (weights[target] ?? 0) * hit.exactness
    if (rank <= bestRank) return
    bestRank = rank
    best = { own, target, kind: hit.kind, exactness: hit.exactness }
  }

  for (const point of Object.keys(pointsB) as CompatPoint[]) consider('asc', point)
  for (const point of Object.keys(pointsA) as CompatPoint[]) consider(point, 'asc')

  return best
}

/** Аспект, который сильнее всего влияет на сферу, — им разбор объясняет её процент. */
export interface SphereAspect {
  own: CompatPoint
  target: CompatPoint
  kind: AspectKind
  /** 0..1, насколько угол близок к точному. */
  exactness: number
}

export interface SphereScore {
  key: string
  value: number
  /** null — аспекта в сфере нет или расчёт идёт только по знаку. */
  aspect: SphereAspect | null
}

export interface CalcSphereScoresInput {
  type: CompatType
  sphereKeys: readonly string[]
  signsA: CompatSigns
  signsB: CompatSigns
  positionsA?: CompatPositions | null
  positionsB?: CompatPositions | null
  ascA?: number | null
  ascB?: number | null
  /**
   * Оба поля больше не участвуют в расчёте и оставлены, чтобы не переписывать вызовы разом.
   * `overall` был опорой выдуманного разброса, `seed` — его источником; теперь сфера считается
   * по своим парам и в подпорках не нуждается.
   *
   * @deprecated удалить вместе с вызовами.
   */
  overall?: number
  /** @deprecated см. `overall`. */
  seed?: number
}

/**
 * Проценты по сферам линзы — те же величины, из которых собран общий процент.
 *
 * Каждая сфера считается по своим парам точек (`getSpherePairs`): чувства по Луне и Венере,
 * страсть по Венере и Марсу, разговоры по Меркурию, первое впечатление по асценденту.
 * Итог — их средневзвешенное (`calcCompatScore`), поэтому шкалы и общий процент больше
 * не могут спорить друг с другом: целое собрано ровно из показанных частей.
 *
 * Прежнего разброса вокруг общего числа в режиме «только знак» тут больше нет. Он был
 * выдуманным: сферы получали `overall ± случайное`, то есть человеку показывали числа,
 * которых расчёт не производил. Теперь без градусов сфера честно считается по одним знакам.
 */
export const calcSphereScores = (input: CalcSphereScoresInput): SphereScore[] =>
  calcSphereBases(input.type, input).map((sphere) => ({
    key: sphere.key,
    value: toPercent(sphere.base),
    aspect: sphere.aspect,
  }))

/**
 * Ступени приговора. `30-34` появилась вместе с растянутой шкалой: до неё низ был недостижим,
 * и обе нижние ступени существовали только в реестре текстов.
 */
export type VerdictRange = '90+' | '70-89' | '50-69' | '35-49' | '30-34'

/** Диапазон вердикта по проценту совместимости (05_compatibility.md, низкий процент — пол 35%). */
export const getVerdictRange = (score: number): VerdictRange => {
  if (score >= 90) return '90+'
  if (score >= 70) return '70-89'
  if (score >= 50) return '50-69'
  if (score >= 35) return '35-49'
  return '30-34'
}

// Сферы разбора по линзе (05_compatibility.md, таблица шага 1). Ключи — стабильные
// идентификаторы, текст подписей живёт в modules/lunya/copy на фронте и в промпте на бэке.
const COMPAT_SPHERES_BY_TYPE: Record<CompatType, readonly string[]> = {
  friend: ['why_clicked', 'who_annoys', 'general_vibe', FIRST_IMPRESSION_SPHERE],
  partner: ['feelings', 'passion', 'daily_life', 'conversations', FIRST_IMPRESSION_SPHERE],
  crush: ['is_there_a_chance', 'how_he_works', 'when_to_approach', FIRST_IMPRESSION_SPHERE],
  ex: ['why_it_didnt_work', 'what_it_was', FIRST_IMPRESSION_SPHERE],
  colleague: ['work_together', 'who_annoys_on_calls', FIRST_IMPRESSION_SPHERE],
  boss: ['how_reads_you', 'when_to_ask_raise', 'when_to_avoid', FIRST_IMPRESSION_SPHERE],
  mother: ['understanding', 'boundaries', 'warmth', FIRST_IMPRESSION_SPHERE],
  father: ['understanding', 'respect', 'unspoken', FIRST_IMPRESSION_SPHERE],
  sibling: ['shared_language', 'rivalry', 'support', FIRST_IMPRESSION_SPHERE],
  relative: ['common_ground', 'friction', FIRST_IMPRESSION_SPHERE],
}

export const getSpheresByCompatType = (type: CompatType): readonly string[] =>
  COMPAT_SPHERES_BY_TYPE[type]

/**
 * Русские подписи сфер (спека 05 «Шаг 1», таблица линз). Живут в контрактах, а не на фронте:
 * их читает и разбор Луни на бэке — он называет сильную и слабую сферу словами, а не ключами.
 */
const SPHERE_LABEL_BY_TYPE: Record<CompatType, Record<string, string>> = {
  friend: {
    why_clicked: 'Почему вы спелись',
    who_annoys: 'Кто кого бесит',
    general_vibe: 'Общий вайб',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
  partner: {
    feelings: 'Чувства',
    passion: 'Страсть',
    daily_life: 'Быт',
    conversations: 'Разговоры',
    [FIRST_IMPRESSION_SPHERE]: 'Химия',
  },
  crush: {
    is_there_a_chance: 'Есть ли шанс',
    how_he_works: 'Как он устроен',
    when_to_approach: 'Когда подкатывать',
    [FIRST_IMPRESSION_SPHERE]: 'Химия',
  },
  ex: {
    why_it_didnt_work: 'Почему не срослось',
    what_it_was: 'Что это было',
    [FIRST_IMPRESSION_SPHERE]: 'Химия',
  },
  colleague: {
    work_together: 'Работа в паре',
    who_annoys_on_calls: 'Кто кого бесит на созвонах',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
  boss: {
    how_reads_you: 'Как вас читает',
    when_to_ask_raise: 'Когда просить повышение',
    when_to_avoid: 'Когда не подходить',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
  mother: {
    understanding: 'Понимание',
    boundaries: 'Границы',
    warmth: 'Тепло',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
  father: {
    understanding: 'Понимание',
    respect: 'Уважение',
    unspoken: 'О чём молчите',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
  sibling: {
    shared_language: 'Общий язык',
    rivalry: 'Соперничество',
    support: 'Поддержка',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
  relative: {
    common_ground: 'Что общего',
    friction: 'Что искрит',
    [FIRST_IMPRESSION_SPHERE]: 'С первого взгляда',
  },
}

/** Подпись сферы для линзы. Фолбэк на сам ключ — на случай будущих сфер. */
export const getSphereLabel = (type: CompatType, sphereKey: string): string =>
  SPHERE_LABEL_BY_TYPE[type][sphereKey] ?? sphereKey

/** Стихия знака — нужна разбору, чтобы объяснить пару словами, а не процентом. */
export const getSignElement = (sign: ZodiacSign): Element => ELEMENT_BY_SIGN[sign]

/**
 * Как стихии пары соотносятся: своя / питающая / в трении. Пороги — из `ELEMENT_COMPAT`:
 * 0.9 — одна стихия, 0.8–0.85 — питают друг друга, ниже — трение.
 */
export type ElementPairKind = 'same' | 'feeding' | 'tense'

export const getElementPairKind = (a: ZodiacSign, b: ZodiacSign): ElementPairKind => {
  const score = ELEMENT_COMPAT[ELEMENT_BY_SIGN[a]][ELEMENT_BY_SIGN[b]]
  if (score >= 0.9) return 'same'
  if (score >= 0.8) return 'feeding'
  return 'tense'
}
